import { useOnValueChange } from '@calendly/hooks'
import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { BodyText } from '@calendly/ui/components/typography'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { FixedOption, MovingOption, NoticePeriodForm, useValidateOnChange } from 'components/event_type_edit'
import { getPrettyDateRange } from 'components/event_type_edit/booking_range/get_pretty_date_range'
import { UnlimitedOption } from 'components/event_type_edit/booking_range/unlimited_option'
import { useToggleState } from 'hooks/useToggleState'
import { dateNotationSelector } from 'selectors/current_user'

import { useDebouncedFormContext, useErrorAfterInitialTouch } from '../../hooks'
import { PanelContentContainer } from '../styles'
import { TextButton } from '../text_button'
import { getNoticeSummary } from './helpers'
import { PanelContentTitle } from './styles'

// i18n-tasks-use t('publisher.event_type_editor.sections.availability.booking_range.schedule.fixed.label')
// i18n-tasks-use t('publisher.event_type_editor.sections.availability.booking_range.schedule.rollingDays.label')
// i18n-tasks-use t('publisher.event_type_editor.sections.availability.booking_range.schedule.rollingDays.business')
// i18n-tasks-use t('publisher.event_type_editor.sections.availability.booking_range.schedule.rollingDays.calendar')
// i18n-tasks-use t('publisher.event_type_editor.sections.availability.booking_range.schedule.unlimited.label')
const dateRangeButtonText = ({ periodType, movingType, maxBookingTime, startDate, endDate, dateNotation }) => {
  switch (periodType) {
    case 'rollingDays': {
      const movingTypeLabel = maxBookingTime > 1 ? 'other' : 'one'
      return I18n.t(
        `publisher.event_type_editor.sections.availability.booking_range.schedule.${periodType}.${movingType}.${movingTypeLabel}`,
        { count: maxBookingTime }
      )
    }
    case 'fixed': {
      return getPrettyDateRange({ startDate, endDate, dateNotation })
    }
    case 'unlimited': {
      return I18n.t('publisher.event_types.availability.booking_range_fieldset.unlimited_label').toLowerCase()
    }
    default: {
      return null
    }
  }
}


const DateRange = () => (
  <PanelContentContainer subsection>
    <MovingOption spaceless />
    <FixedOption spaceless />
    <UnlimitedOption spaceless />
  </PanelContentContainer>
)

export const Range = ({ isLocked = false }) => {
  const {
    values: { periodType, startDate, endDate, movingType, maxBookingTime, minBookingTime, minBookingTimeType },
  } = useDebouncedFormContext()

  const dateNotation = useSelector(dateNotationSelector)
  const [isDateRangeOpen, toggleDateRange] = useToggleState(false)
  const [isNoticeOpen, toggleNotice] = useToggleState(false)

  const DateRangePickerProxy = useCallback((props) => (
    <TextButton
      aria-label={I18n.t('publisher.event_type_editor.sections.availability.booking_range.label')}
      isOpen={isDateRangeOpen}
      showChevron={!isLocked}
      disabled={isLocked}
      onClick={isLocked ? () => {} : toggleDateRange}
      {...props}
    />
  ), [isLocked, isDateRangeOpen, toggleDateRange])

  const MinimumNoticePickerProxy = useCallback((props) => (
    <TextButton
      aria-label={I18n.t('publisher.event_type_editor.sections.scheduling_settings.event_limits.notice.title')}
      isOpen={isNoticeOpen}
      showChevron={!isLocked}
      disabled={isLocked}
      onClick={isLocked ? () => {} : toggleNotice}
      {...props}
    />
  ), [isLocked, isNoticeOpen, toggleNotice])

  useValidateOnChange('startDate')
  useValidateOnChange('minBookingTime')
  useErrorAfterInitialTouch('startDate')

  useOnValueChange(isDateRangeOpen, () => {
    if (isDateRangeOpen && isNoticeOpen) toggleNotice()
  })

  useOnValueChange(isNoticeOpen, () => {
    if (isNoticeOpen && isDateRangeOpen) toggleDateRange()
  })

  return (
    <PanelContentContainer section data-testid="range-group">
      <PanelContentTitle>
        {I18n.t('publisher.event_type_editor.sections.availability.booking_range.title')}
      </PanelContentTitle>
      <BodyText size="medium">
        {
          HtmlParser(I18n.t(`publisher.event_type_editor.sections.availability.booking_range.schedule.${periodType}.label`, {
            range_text: dateRangeButtonText({ periodType, movingType, maxBookingTime, startDate, endDate, dateNotation }),
            notice_text: getNoticeSummary(minBookingTime, minBookingTimeType)
          }), { DateRangePicker: DateRangePickerProxy, NoticePeriodPicker: MinimumNoticePickerProxy })
        }
      </BodyText>
      {isDateRangeOpen && <DateRange />}
      {isNoticeOpen && (
        <PanelContentContainer subsection>
          <NoticePeriodForm />
        </PanelContentContainer>
      )}
    </PanelContentContainer>
  )
}
